import React, { useState } from 'react';
import { useModalA11y } from '../../hooks/useModalA11y';
import { toast } from '../Toast/Toast';
import './SubscriberPreviewModal.css';

export interface SubscriberPreview {
  id: string;
  display_name?: string;
  email?: string;
  avatar?: string | null;
  plan_type?: string;
  subscription_status?: string;
  is_subscribed?: number | boolean;
  started_at?: string | null;
  expires_at?: string | null;
  paid_at?: string | null;
  amount_paid?: number | null;
  created_at?: string;
}

interface Props {
  sub: SubscriberPreview;
  onClose: () => void;
  onMessage?: (userId: string) => void;
  onApprove?: (userId: string) => void;
  onRevoke?: (userId: string) => void;
}

const fmtDate = (s?: string | null) =>
  s ? new Date(s).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const SubscriberPreviewModal: React.FC<Props> = ({ sub, onClose, onMessage, onApprove, onRevoke }) => {
  const isActive = sub.is_subscribed === 1 || sub.is_subscribed === true || sub.subscription_status === 'active';
  const status = sub.subscription_status || (isActive ? 'active' : 'pending');
  const initial = ((sub.display_name || sub.email || 'U')[0] || 'U').toUpperCase();
  const [avatarBroken, setAvatarBroken] = useState(false);
  const dialogRef = useModalA11y({ onEscape: onClose });

  // Loyalty signal — how long the fan has been a subscriber. Computed
  // from the earliest available start signal (started_at → created_at).
  const startISO = sub.started_at || sub.created_at;
  const daysSubscribed = startISO
    ? Math.floor((Date.now() - new Date(startISO).getTime()) / 86_400_000)
    : null;
  const loyaltyLabel = daysSubscribed === null
    ? null
    : daysSubscribed < 1
      ? 'Joined today'
      : daysSubscribed === 1
        ? '1 day'
        : daysSubscribed < 30
          ? `${daysSubscribed} days`
          : daysSubscribed < 365
            ? `${Math.floor(daysSubscribed / 30)} mo`
            : `${(daysSubscribed / 365).toFixed(1)} yr`;

  // Days remaining on the current period. Negative = already expired
  // (the cron may not have flipped status='expired' yet).
  const daysLeft = sub.expires_at
    ? Math.ceil((new Date(sub.expires_at).getTime() - Date.now()) / 86_400_000)
    : null;
  const expiryLabel = sub.plan_type === 'lifetime'
    ? 'Lifetime — no expiry'
    : daysLeft === null
      ? '—'
      : daysLeft < 0
        ? `${Math.abs(daysLeft)}d ago`
        : daysLeft === 0
          ? 'Today'
          : `${daysLeft}d left`;

  const copyEmail = async () => {
    if (!sub.email) return;
    try {
      await navigator.clipboard?.writeText(sub.email);
      toast.success('Email copied');
    } catch {
      toast.error('Could not copy email');
    }
  };

  return (
    <div className="oj-modal-backdrop" onClick={onClose}>
      <div
        className="oj-modal subscriber-preview-modal"
        onClick={e => e.stopPropagation()}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${sub.display_name || 'Subscriber'} details`}
        tabIndex={-1}
      >
        <button className="oj-modal-close" onClick={onClose} aria-label="Close">×</button>

        <div className="sp-header">
          {sub.avatar && !avatarBroken ? (
            <img
              src={sub.avatar}
              alt=""
              className="sp-avatar"
              onError={() => setAvatarBroken(true)}
            />
          ) : (
            <div className="sp-avatar sp-avatar-initials">{initial}</div>
          )}
          <div className="sp-identity">
            <h2>{sub.display_name || 'Unnamed fan'}</h2>
            <p className="sp-email">
              {sub.email || '—'}
              {sub.email && (
                <button
                  type="button"
                  className="sp-copy-email"
                  onClick={copyEmail}
                  title="Copy email"
                  aria-label="Copy email"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                  </svg>
                </button>
              )}
            </p>
            <div className="sp-identity-badges">
              <span className={`sp-badge sp-badge-${status}`}>{status.toUpperCase()}</span>
              {loyaltyLabel && (
                <span className="sp-badge sp-badge-loyalty" title="Time as a subscriber">{loyaltyLabel}</span>
              )}
            </div>
          </div>
        </div>

        <div className="sp-grid">
          <div className="sp-cell">
            <span className="sp-label">Plan</span>
            <span className="sp-value">{(sub.plan_type || '—').toString().toUpperCase()}</span>
          </div>
          <div className="sp-cell">
            <span className="sp-label">Subscribed</span>
            <span className="sp-value">{fmtDate(sub.started_at || sub.created_at)}</span>
          </div>
          <div className="sp-cell">
            <span className="sp-label">Expires</span>
            <span className="sp-value">
              {fmtDate(sub.expires_at)}
              {sub.plan_type !== 'lifetime' && daysLeft !== null && (
                <span className={`sp-value-sub ${daysLeft <= 7 && daysLeft >= 0 ? 'sp-warn' : ''} ${daysLeft < 0 ? 'sp-danger' : ''}`}>
                  {expiryLabel}
                </span>
              )}
              {sub.plan_type === 'lifetime' && (
                <span className="sp-value-sub">{expiryLabel}</span>
              )}
            </span>
          </div>
          <div className="sp-cell">
            <span className="sp-label">Last Payment</span>
            <span className="sp-value">
              {sub.amount_paid != null && Number(sub.amount_paid) > 0 ? `$${Number(sub.amount_paid).toFixed(2)}` : '—'}
              {sub.paid_at ? ` • ${fmtDate(sub.paid_at)}` : ''}
            </span>
          </div>
        </div>

        <div className="sp-actions">
          {onMessage && (
            <button className="sp-action-primary" onClick={() => onMessage(sub.id)}>
              💬 Message
            </button>
          )}
          {onApprove && (
            <button
              className="sp-action-secondary"
              onClick={() => onApprove(sub.id)}
              disabled={isActive}
              title={isActive ? 'Already active' : (status === 'cancelled' ? 'Reactivate this subscription' : 'Approve pending subscription')}
            >
              {status === 'cancelled' ? 'Reinstate' : 'Approve'}
            </button>
          )}
          {onRevoke && (
            <button
              className="sp-action-danger"
              onClick={() => onRevoke(sub.id)}
              disabled={!isActive}
              title={isActive ? 'Cancel this subscription' : 'Not active'}
            >
              Revoke
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriberPreviewModal;
